import { html } from '@nrk/dh-server-libs/html'
import Scrollhistorie from '@nrk/dh-basis-scrollhistorie'
import { closest } from '@nrk/dh-basis-common'
import { balanceAndWatch } from '../lib/balance-text-wrapper.js'
import { debounce } from 'debounce'

/* styles */
import styles from './introscroll.module.css'
import backgroundStyles from './background.module.css'
import cardStyles from './cards/cards.module.css'

const s = { ...styles, ...backgroundStyles, ...cardStyles }

function toArray(nodeList) {
  return [].slice.call(nodeList)
}

export default class Introscroll extends Scrollhistorie {
  static get observedAttributes() {
    return ['background-color', ...super.observedAttributes]
  }

  _updating = false
  _viewport = { height: 0, width: 0 }
  _currentPercentage

  connectedCallback() {
    super.connectedCallback()
    this._updateSizes()
    this._debouncedUpdateSizes = debounce(this._updateSizes, 500)
    window.addEventListener('resize', this._debouncedUpdateSizes)
    // eslint-disable-next-line compat/compat
    if (document?.fonts?.ready?.then) {
      // eslint-disable-next-line promise/catch-or-return
      document.fonts.ready.then(this._updateTitle)
    }

    this.updateByline()

    // Date in byline is calculated on the client. Update byline to include new date.
    setTimeout(() => this.updateByline(), 100)
    setTimeout(() => this.updateByline(), 5000)

    toArray(this.querySelectorAll('img, video, [role=img]')).forEach((elm) => {
      elm.tabIndex = '-1'
    })
    this.addEventListener('focus', this._handleFocus, true)

    /*
    Polyfill text-wrap: balance.
    TODO: remove once Safari fully supports text-wrap: balance
    (There's currently a bug in Safari that disables text-wrap balance when used with box-decoration)
    https://bugs.webkit.org/show_bug.cgi?id=274145
    */
    if (!CSS.supports('text-wrap', 'balance') || navigator?.userAgent?.includes?.('Safari')) {
      requestAnimationFrame(() => {
        balanceAndWatch(this.querySelectorAll('.balance-text'))
      })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('focus', this._handleFocus, true)
    window.removeEventListener('resize', this._debouncedUpdateSizes)
  }

  attributeChangedCallback(name, prevValue, newValue) {
    super.attributeChangedCallback(name, prevValue, newValue)

    if (name === 'background-color' && typeof newValue === 'string') {
      this.style.setProperty('--introscroll-background-color', newValue)
    } else if (name === 'viewport-position') {
      /* TODO: remove once Safari supports scroll-driven animations */
      if (!CSS.supports('animation-timeline', 'view()')) {
        this._updateVisiblePercentage()
      }
    }
  }

  _handleFocus = (e) => {
    const backgroundElement = e.target && closest(e.target, '[data-background-id]')
    if (!backgroundElement) {
      return
    }

    const backgroundId = backgroundElement.getAttribute('data-background-id')
    if (this.activeBackgroundId !== backgroundId) {
      const triggerElement = this.triggerElements.find(
        (elm) => elm.getAttribute('data-show-background') === backgroundId,
      )
      if (triggerElement) {
        window.scrollTo(0, triggerElement.offsetTop - window.innerHeight / 2)
      }
    }
  }

  /**
   * Update how many percent of the screen is filled with introscroll.
   * Used to fade out and translate up the last card for browsers that doesn't support scroll-driven animations.
   *
   * TODO: Remove once Safari supports scroll-driven animations
   *
   * @private
   */
  _updateVisiblePercentage() {
    if (this._updating) {
      return
    }
    this._updating = true

    const loop = () => {
      if (this.viewportPosition !== 'IN-VIEWPORT') {
        this._updating = false
      } else {
        const bottom = Math.min(
          Math.max(this.offsetHeight - window.scrollY, 0),
          this._viewport.height,
        )
        const percentage = bottom / this._viewport.height
        if (percentage !== this._currentPercentage) {
          this.style.setProperty('--introscroll-percentage', percentage.toFixed(3))
          this._currentPercentage = percentage
        }
        requestAnimationFrame(loop)
      }
    }
    requestAnimationFrame(loop)
  }

  updateByline() {
    const authors = toArray(document.querySelectorAll('.author__body')).map((authorElm) => {
      const authNameElm = authorElm.querySelector('.author__name')
      const authRoleElm = authorElm.querySelector('.author__role')
      return {
        name: Array.from(authNameElm.childNodes ?? [])
          .filter((n) => n.nodeName === '#text')
          .map((n) => n.textContent)
          .join('')
          .trim(),
        href: authNameElm.href,
        role: authRoleElm.textContent,
      }
    })
    const container = this.querySelector(`.${s.byline}`)
    const publishDateElm = document.querySelector('.article-dateline__full')
    const publishDate = publishDateElm ? publishDateElm.textContent : ''

    const annotationElm = document.querySelector('.article-dateline-annotation')
    const publishAnnotation = annotationElm ? annotationElm.textContent : ''
    const expandable = authors.length > 3

    const authorHTML = authors.map((author) => {
      return html`
        <div class="${s.author}">
          <a class="${s.author__name}" href="${author.href}" itemprop="author">${author.name}</a
          >&nbsp;
          <span class="${s.author__role}">${author.role}</span>
        </div>
      `
    })
    const expandHTML = html`
      <a id="intro-authors--expand" href="#intro-authors--expand">
        <span class="nrk-sr">Vis ${authors.length - 1} bidragsytere til</span>
        <span aria-hidden="true">+${authors.length - 1} til</span>
      </a>
    `
    authorHTML.splice(1, 0, expandHTML)

    container.innerHTML = html`
      <div class="${s.authors} ${expandable ? s.expandable : ''}">${authorHTML.join('')}</div>
      <span class="${s.publish__date}"> ${publishAnnotation} ${publishDate} </span>
    `
  }

  _updateSizes = () => {
    if (this._viewport.height !== document.documentElement.clientHeight) {
      this._viewport.height = document.documentElement.clientHeight
    }

    if (this._viewport.width !== document.documentElement.clientWidth) {
      this._viewport.width = document.documentElement.clientWidth
      this._updateTitle()
      this._updateCardHeights()
    }
  }

  _updateCardHeights() {
    const triggerElements = this.triggerElements
    if (!triggerElements || !triggerElements.length) {
      return
    }

    const containerSelector = `.${s.textContainer}`
    const lastElement = triggerElements[triggerElements.length - 1]
    const lastCardHeight = lastElement?.querySelector(containerSelector)?.offsetHeight

    requestAnimationFrame(() => {
      this.style.setProperty('--introscroll-last-card-height', `${lastCardHeight}px`)
    })
  }

  _updateTitle = () => {
    const titleCardContainer = this.querySelector(`.${s.titleCard} .${s.textContainer}`)
    let fontReduce = 0
    this.style.setProperty('--introscroll-font-reduce', `${fontReduce}px`)
    while (titleCardContainer.scrollWidth > titleCardContainer.offsetWidth && fontReduce < 20) {
      fontReduce++
      this.style.setProperty('--introscroll-font-reduce', `${fontReduce}px`)
    }
  }

  get backgroundColor() {
    this.getAttribute('background-color')
  }

  set backgroundColor(val) {
    this.setAttribute('background-color', val)
  }
}
